import { useEffect, useRef, useState } from "react";
import { RefreshCw, Wifi, WifiOff, X } from "lucide-react";
import { Button } from "./Button";

/**
 * Aviso de conexão: sem internet, uma faixa fixa explica que as telas voltam sozinhas; quando a conexão volta, um aviso
 * curto confirma (as consultas se atualizam pelo próprio react-query ao reconectar).
 */
export function ConnectionStatus() {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine !== false));
  const [back, setBack] = useState(false);
  const wasOffline = useRef(false);

  useEffect(() => {
    const on = () => {
      setOnline(true);
      if (wasOffline.current) setBack(true);
      wasOffline.current = false;
    };
    const off = () => {
      setOnline(false);
      setBack(false);
      wasOffline.current = true;
    };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  useEffect(() => {
    if (!back) return;
    const t = window.setTimeout(() => setBack(false), 3500);
    return () => window.clearTimeout(t);
  }, [back]);

  if (online && !back) return null;
  return (
    <div className={online ? "vx-netstat vx-netstat--ok" : "vx-netstat"} role="status" aria-live="polite">
      {online ? <Wifi aria-hidden="true" /> : <WifiOff aria-hidden="true" />}
      <span>
        {online
          ? "Conexão restabelecida. Os dados foram atualizados."
          : "Sem conexão com a internet. O que estiver na tela continua aqui e tudo se atualiza quando a conexão voltar."}
      </span>
    </div>
  );
}

/**
 * Nova versão publicada: cartão discreto no canto com "Atualizar agora" (recarrega a página) e "Depois" (esconde até a
 * próxima versão). Quem decide que há versão nova é o app (veja watchNewVersion).
 */
export function UpdateNotice({ open, onReload, onDismiss }: { open: boolean; onReload: () => void; onDismiss: () => void }) {
  if (!open) return null;
  return (
    <div className="vx-update" role="status" aria-live="polite">
      <span className="vx-update__icon" aria-hidden="true">
        <RefreshCw />
      </span>
      <div className="vx-update__text">
        <p className="vx-update__title">Nova versão do Vixio OS</p>
        <p className="text-2">Atualize para receber as melhorias. O que você não salvou nesta tela se perde.</p>
        <div className="vx-row" style={{ marginTop: 8 }}>
          <Button size="sm" variant="primary" onClick={onReload}>
            Atualizar agora
          </Button>
          <Button size="sm" variant="ghost" onClick={onDismiss}>
            Depois
          </Button>
        </div>
      </div>
      <Button className="vx-update__close" size="sm" variant="ghost" iconOnly label="Fechar aviso de nova versão" icon={<X />} onClick={onDismiss} />
    </div>
  );
}

/** Script principal citado num index.html (o nome muda a cada build: /assets/index-<hash>.js). */
export function mainScriptOf(html: string): string | null {
  const m = /<script[^>]+type="module"[^>]+src="([^"]*\/assets\/[^"]+\.js)"/.exec(html) ?? /<script[^>]+src="([^"]*\/assets\/[^"]+\.js)"[^>]*type="module"/.exec(html);
  return m?.[1] ?? null;
}

/**
 * Observa se o index.html publicado aponta para um script principal diferente do que está rodando: confere ao voltar
 * para a aba e a cada `intervalMs` (padrão 5 min). Sem script com hash (desenvolvimento) não faz nada. Devolve a
 * função que para a observação.
 */
export function watchNewVersion(onNew: () => void, intervalMs = 5 * 60_000): () => void {
  if (typeof document === "undefined") return () => undefined;
  const current = mainScriptOf(document.documentElement.outerHTML);
  if (!current) return () => undefined;
  let stopped = false;
  let notified = false;
  const check = async () => {
    if (stopped || notified || document.visibilityState === "hidden") return;
    try {
      const res = await fetch(`/index.html?v=${Date.now()}`, { cache: "no-store", credentials: "same-origin" });
      if (!res.ok) return;
      const next = mainScriptOf(await res.text());
      if (next && next !== current) {
        notified = true;
        onNew();
      }
    } catch {
      /* sem rede: tenta de novo depois */
    }
  };
  const timer = window.setInterval(() => void check(), intervalMs);
  const onVisible = () => {
    if (document.visibilityState === "visible") void check();
  };
  document.addEventListener("visibilitychange", onVisible);
  return () => {
    stopped = true;
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
